/* eslint max-statements: ["error", {max: 30}]  -- Due to long file */
/* eslint max-lines-per-function: ["error", {max: 175}]  -- Due to long file */
import { useEffect, useMemo, useRef } from "react";

import isNil from "lodash/isNil";
import { useWindowDimensions } from "react-native";

import { DefaultTabScreenOptions, TabPath } from "@/constants/Navigation";
import { NavTestID } from "@/constants/TestID";
import { WebTabBarMinWidth } from "@/constants/UI";
import { Tabs } from "@/crossPlatform/expo-router";
import { ChatChannelContextProvider } from "@/hooks/useChatChannels";
import { useColorToken } from "@/hooks/useColorToken";
import useHelpCenter from "@/hooks/useHelpCenter";
import usePushNotifications from "@/hooks/usePushNotifications";
import usePushTokenRegistration from "@/hooks/usePushTokenRegistration";
import useSendbirdSDK from "@/hooks/useSendbirdSDK";
import useTabScreens from "@/hooks/useTabScreens";
import useUserIdentitySync from "@/hooks/useUserIdentitySync";
import { useAnalytics } from "@/services/Analytics";
import useAppMonitor from "@/services/AppMonitor";
import { useGetUserChatTokenQuery } from "@/store/api/chat";
import { useGetBasicInfoQuery, useGetFullProfileQuery } from "@/store/api/user";
import LiveEventTabHeader from "@/ui/components/home/LiveEventTabHeader";
import createWebTabBar from "@/ui/components/navigation/WebTabBar";
import AppointmentConfirmationAlert from "@/ui/components/schedule/booking/steps/AppointmentConfirmationAlert";
import SMTUploadBannerHeader from "@/ui/components/smt/SMTUploadBannerHeader";
import { Icon, Text } from "@/ui/core";
import { ColorToken } from "@/ui/theme/tokens";
import { Font } from "@/ui/theme/typography";
import { toMs } from "@/utils/date.util";
import { shouldShowStorybook } from "@/utils/navigation.util";
import { isWeb } from "@/utils/platform.util";
import { isNotNilNotEmpty } from "@/utils/shared.utils";
import { joinTestIDs } from "@/utils/string.util";
import { getIsOnboardedOnMobile } from "@/utils/user.util";

const DeferIdentifyHelpCenterDurationMs = toMs({ seconds: 2 });

const TabLayout = () => {
  const { captureException } = useAppMonitor();
  const { track } = useAnalytics();

  const { syncUserIdentity } = useUserIdentitySync();
  const { connectSendbird, isSendbirdConnected } = useSendbirdSDK();

  const { registerPushToken } = usePushTokenRegistration();
  const { pushToken } = usePushNotifications();
  const { data: basicInfoData, isSuccess: isBasicInfoSuccess } =
    useGetBasicInfoQuery();
  const { isSuccess: isSendbirdChatTokenSuccess } = useGetUserChatTokenQuery();
  const { data: fullProfileData } = useGetFullProfileQuery();
  const { identifyHelpCenter } = useHelpCenter();

  // the custom WebTabBar can't shrink to fit a narrow viewport, so we fall back to
  // the native-style tab bar, which distributes and shrinks its columns to fit
  const { width: windowWidth } = useWindowDimensions();
  const shouldUseWebTabBar = isWeb() && windowWidth >= WebTabBarMinWidth;

  const tabScreens = useTabScreens();
  const webTabBar = useMemo(() => {
    const iconMap = Object.fromEntries(
      tabScreens.map((screen) => [screen.pathName, screen.icon]),
    );
    return createWebTabBar(iconMap);
  }, [tabScreens]);
  const isHelpcenterUserIdentified = useRef(false);

  const badgeBackgroundColor = useColorToken({
    colorKey: ColorToken.TertiaryOrangeBase,
  });
  const badgeTextColor = useColorToken({
    colorKey: ColorToken.NeutralBackgroundPrimary,
  });
  const tabBarActiveTintColor = useColorToken({
    colorKey: ColorToken.AccentBlue,
  });
  const tabBarInactiveTintColor = useColorToken({
    colorKey: ColorToken.NeutralPrimary,
  });

  const userId = basicInfoData?.externalId;

  useEffect(() => {
    if (isSendbirdConnected) return;
    if (!isBasicInfoSuccess) return;
    if (!isSendbirdChatTokenSuccess) return;
    if (!isNotNilNotEmpty(userId)) return;
    connectSendbird(userId).catch((error) => {
      captureException(error);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- including connectSendbird in dependencies causes errors
  }, [
    isBasicInfoSuccess,
    isSendbirdChatTokenSuccess,
    isSendbirdConnected,
    basicInfoData,
    captureException,
    userId,
  ]);

  useEffect(() => {
    if (!isSendbirdConnected) return;
    if (!pushToken) return;
    registerPushToken(pushToken).catch((error) => {
      captureException(error);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- including connectSendbird and registerPushToken in dependencies causes errors
  }, [isSendbirdConnected, pushToken]);

  useEffect(() => {
    if (isNil(fullProfileData) || isHelpcenterUserIdentified.current) return;
    const deferTimeoutId = setTimeout(() => {
      isHelpcenterUserIdentified.current = true;
      void identifyHelpCenter();
    }, DeferIdentifyHelpCenterDurationMs);
    return () => clearTimeout(deferTimeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- identifyHelpCenter is intentionally omitted to prevent multiple calls
  }, [fullProfileData]);

  useEffect(() => {
    if (!isBasicInfoSuccess || !isNotNilNotEmpty(userId)) return;
    if (isNil(basicInfoData)) return;

    // don't sync identity if user is not onboarded - prevents Braze auth errors
    // for users who are still in the onboarding flow
    const isUserOnboarded = getIsOnboardedOnMobile(basicInfoData.onboardings);
    if (!isUserOnboarded) return;
    syncUserIdentity({ userId });
  }, [isBasicInfoSuccess, userId, basicInfoData, syncUserIdentity]);

  const storybookHref = shouldShowStorybook ? TabPath.Storybook : null;

  return (
    <ChatChannelContextProvider isConnected={isSendbirdConnected}>
      <LiveEventTabHeader />
      <SMTUploadBannerHeader />
      <Tabs
        detachInactiveScreens
        screenOptions={{
          ...DefaultTabScreenOptions,
          tabBarActiveTintColor,
          tabBarInactiveTintColor,
        }}
        tabBar={shouldUseWebTabBar ? webTabBar : undefined}
      >
        {tabScreens.map((screen) => {
          type ColorProp = { color: string };
          const tabBarIcon = ({ color }: ColorProp) => (
            <Icon
              color={color}
              name={screen.icon}
              size="md"
              testID={joinTestIDs(NavTestID.Tab, screen.pathName, "icon")}
            />
          );

          // eslint-disable-next-line @typescript-eslint/naming-convention
          type FocusedProp = { focused: boolean };
          // eslint-disable-next-line @typescript-eslint/naming-convention
          const tabBarLabel = ({ focused }: FocusedProp) => (
            <Text
              color={focused ? tabBarActiveTintColor : tabBarInactiveTintColor}
              testID={joinTestIDs("tab", screen.title, "label")}
              variant="overlineMedium"
            >
              {screen.title}
            </Text>
          );

          return (
            <Tabs.Screen
              key={screen.pathName}
              listeners={{
                tabPress: () => {
                  track(screen.analyticsEvent, { Tab: screen.pathName });
                },
              }}
              name={screen.pathName}
              options={{
                tabBarBadge: screen.notificationCount,
                tabBarBadgeStyle: {
                  backgroundColor: badgeBackgroundColor,
                  color: badgeTextColor,
                  fontFamily: Font.BodyMedium,
                  fontSize: 12,
                  marginLeft: 4,
                },
                tabBarButtonTestID: joinTestIDs(
                  "tab",
                  screen.title.toLowerCase(),
                ),
                tabBarIcon,
                tabBarLabel,
                title: screen.title,
              }}
            />
          );
        })}
        <Tabs.Screen
          name={TabPath.Storybook}
          options={{ href: storybookHref }}
        />
      </Tabs>
      <AppointmentConfirmationAlert />
    </ChatChannelContextProvider>
  );
};

export { DeferIdentifyHelpCenterDurationMs };
export default TabLayout;
